import { useQuery } from 'react-query'
import type { QueryClient } from 'react-query'
import api from 'balkerne-core/api'
import { METRIC_VIEWS, parseMetricKey } from './views'
import { CanonicalPeriod, MetricState } from '../../components/climateAnalytics/common'
import type { AdaptationView } from '../../components/climateAnalytics/common'
import type { MetricDataFilters } from '../analytics'

/**
 * Translate the frontend adaptation view into the backend `adapted`/`effective`
 * query params. `effective` returns adapted rows where they exist and baseline
 * rows otherwise; `baseline` forces the unadapted rows.
 */
function adaptationViewToParams(view?: AdaptationView | null): { adapted: boolean | null; effective: boolean } {
  return view === 'baseline' ? { adapted: false, effective: true } : { adapted: null, effective: true }
}

export const CLIMATE_DATA_QUERY_KEY_ROOT = '/analytics/cc/metrics/data'

/** Poll cadence while a metric is still being computed (queued/running). */
export const CLIMATE_DATA_POLL_INTERVAL_MS = 3000

/** Whether a metric state represents active, in-progress computation. */
export function isActiveMetricState(state?: MetricState | null): boolean {
  return state === MetricState.QUEUED || state === MetricState.RUNNING
}

type PropertyId = number

export type PropertyResult = {
  property_id: PropertyId
  state: {
    property_id: PropertyId
    metric_id: number
    value: MetricState
    message: string | null
    detail: any | null
    created_at: string | null
    updated_at: string | null
  }
  data: any | null
}

type ClimateDataResponse = Record<PropertyId, PropertyResult>

type PropertyScope = { propertyId: number }
export type PortfolioScope = { groupId?: number; propertyIds?: number[] }

export type QueryParams = {
  emissionTiers?: string[] | null
  aggParams?: Record<string, any> | null
  facetQuery?: Record<string, any> | null
  canonicalPeriods?: CanonicalPeriod[] | null
  useFacetDefaults?: boolean | null
  filters?: MetricDataFilters | null
  view?: AdaptationView | null
}

export function usePropertyClimateData(metricKey: string, scope: PropertyScope, params?: QueryParams) {
  const spec = METRIC_VIEWS[metricKey]
  if (!spec) throw new Error(`Unregistered property view for "${metricKey}"`)

  // Resolve params with precedence: params > spec defaults
  const canonicalPeriods =
    params && Object.prototype.hasOwnProperty.call(params, 'canonicalPeriods')
      ? params.canonicalPeriods
      : spec.canonical_periods ?? null
  const aggParams =
    params && Object.prototype.hasOwnProperty.call(params, 'aggParams') ? params.aggParams ?? {} : spec.agg_params ?? {}
  const facetQuery =
    params && Object.prototype.hasOwnProperty.call(params, 'facetQuery') ? params.facetQuery : spec.facet_query ?? null
  const useFacetDefaults =
    params && Object.prototype.hasOwnProperty.call(params, 'useFacetDefaults')
      ? params.useFacetDefaults ?? false
      : spec.use_facet_defaults
  const hasFiltersParam = params && Object.prototype.hasOwnProperty.call(params, 'filters')
  const { adapted, effective } = adaptationViewToParams(params?.view)

  const body = {
    metric_id: null,
    metric_key: parseMetricKey(metricKey),
    property_ids: [scope.propertyId],
    group_id: null,
    emission_tiers: params?.emissionTiers ?? null,
    canonical_periods: canonicalPeriods,
    use_facet_defaults: useFacetDefaults,
    facet_query: facetQuery,
    agg_params: aggParams,
    adapted,
    effective,
    ...(hasFiltersParam ? { filters: params?.filters ?? null } : {}),
  }

  const paramsKey = JSON.stringify({
    emissionTiers: body.emission_tiers,
    canonicalPeriods: body.canonical_periods,
    useFacetDefaults: body.use_facet_defaults,
    facetQuery: body.facet_query,
    aggParams: body.agg_params,
    adapted: body.adapted,
    effective: body.effective,
    filters: hasFiltersParam ? body.filters : undefined,
  })

  const queryKey = [CLIMATE_DATA_QUERY_KEY_ROOT, 'property', metricKey, scope.propertyId, paramsKey]

  const fetch = async (): Promise<ClimateDataResponse> => {
    const res = await api.post<ClimateDataResponse>(CLIMATE_DATA_QUERY_KEY_ROOT, body)
    return res.data
  }

  return useQuery<ClimateDataResponse, unknown, PropertyResult | undefined>(queryKey, fetch, {
    enabled: scope.propertyId != null && !isNaN(scope.propertyId),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchInterval: (data: PropertyResult | undefined) =>
      isActiveMetricState(data?.state?.value) ? CLIMATE_DATA_POLL_INTERVAL_MS : false,
    select: raw => raw?.[scope.propertyId] ?? undefined,
  })
}

/** Shared query definition for single-hazard pages and parallel overview queries. */
export function portfolioClimateDataQuery(metricKey: string, scope?: PortfolioScope, params?: QueryParams) {
  const spec = METRIC_VIEWS[metricKey]
  if (!spec) throw new Error(`Unregistered portfolio view for "${metricKey}"`)

  // Resolve params with precedence: params > spec defaults
  const canonicalPeriods =
    params && Object.prototype.hasOwnProperty.call(params, 'canonicalPeriods')
      ? params.canonicalPeriods
      : spec.canonical_periods ?? null
  const aggParams =
    params && Object.prototype.hasOwnProperty.call(params, 'aggParams') ? params.aggParams ?? {} : spec.agg_params ?? {}
  const facetQuery =
    params && Object.prototype.hasOwnProperty.call(params, 'facetQuery') ? params.facetQuery : spec.facet_query ?? null
  const useFacetDefaults =
    params && Object.prototype.hasOwnProperty.call(params, 'useFacetDefaults')
      ? params.useFacetDefaults ?? false
      : spec.use_facet_defaults
  const hasFiltersParam = params && Object.prototype.hasOwnProperty.call(params, 'filters')
  const { adapted, effective } = adaptationViewToParams(params?.view)

  const body = {
    metric_id: null,
    metric_key: parseMetricKey(metricKey),
    property_ids: scope?.propertyIds ?? null,
    group_id: scope?.groupId ?? null,
    emission_tiers: params?.emissionTiers ?? null,
    canonical_periods: canonicalPeriods,
    use_facet_defaults: useFacetDefaults,
    facet_query: facetQuery,
    agg_params: aggParams,
    adapted,
    effective,
    ...(hasFiltersParam ? { filters: params?.filters ?? null } : {}),
  }

  const paramsKey = JSON.stringify({
    emissionTiers: body.emission_tiers,
    canonicalPeriods: body.canonical_periods,
    useFacetDefaults: body.use_facet_defaults,
    facetQuery: body.facet_query,
    aggParams: body.agg_params,
    adapted: body.adapted,
    effective: body.effective,
    filters: hasFiltersParam ? body.filters : undefined,
  })

  const queryKey = [
    CLIMATE_DATA_QUERY_KEY_ROOT,
    'portfolio',
    metricKey,
    scope?.groupId ?? null,
    scope?.propertyIds ? scope.propertyIds.join(',') : null,
    paramsKey,
  ]
  const fetch = async (): Promise<ClimateDataResponse> => {
    const res = await api.post<ClimateDataResponse>(CLIMATE_DATA_QUERY_KEY_ROOT, body)
    return res.data
  }

  return {
    queryKey,
    queryFn: fetch,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    refetchInterval: (data: ClimateDataResponse | undefined) =>
      data && Object.values(data).some(result => isActiveMetricState(result?.state?.value))
        ? CLIMATE_DATA_POLL_INTERVAL_MS
        : false,
  }
}

export function usePortfolioClimateData(metricKey: string, scope?: PortfolioScope, params?: QueryParams) {
  return useQuery(portfolioClimateDataQuery(metricKey, scope, params))
}

export function invalidateClimateDataQueries(queryClient: QueryClient, options: { hazardCode?: string | null } = {}) {
  const normalizedHazardCode = options.hazardCode?.trim()

  return queryClient.invalidateQueries({
    predicate: (query: any) => {
      const key = Array.isArray(query.queryKey) ? query.queryKey : [query.queryKey]
      if (key[0] !== CLIMATE_DATA_QUERY_KEY_ROOT) return false

      if (!normalizedHazardCode) return true

      const metricKey = typeof key[2] === 'string' ? key[2] : ''
      return metricKey.startsWith(`${normalizedHazardCode}:`)
    },
    refetchActive: true,
    refetchInactive: false,
  })
}
